import { lazy, Suspense, useState, useRef, useEffect, useCallback } from 'react';
import { useChartStore, SUBPANE_MIN_HEIGHT, CANDLE_MIN_HEIGHT } from '@/store/chartStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { COMPARE_COLORS_EXPORT } from '@/components/Header/CompareBar';
import { trackCompareAction, trackMacroAction, trackMacroVisibility } from '@/services/gtag';
import { INDICATORS, INDICATOR_DEFAULT_COLORS, type IndicatorDef } from '@/components/Chart/indicatorDefs';
import { DomPosition, type Chart } from 'klinecharts';
import { EyeIcon, EyeOffIcon, XIcon, GearIcon } from '@/components/icons';
import { EpsLineConfigModal } from '@/components/Chart/EpsLineConfigModal';
import { CupHandleConfigModal } from '@/components/Chart/CupHandleConfigModal';
import { WilliamOneilSellConfigModal } from '@/components/Chart/WilliamOneilSellConfigModal';
import { ForeignHoldingConfigModal } from '@/components/Chart/ForeignHoldingConfigModal';

// Both pull in KaTeX (~70KB) for formula rendering — split out so it's only
// fetched when a user actually opens one of these config modals.
const IndicatorConfigModal = lazy(() =>
  import('@/components/Chart/IndicatorConfigModal').then((m) => ({ default: m.IndicatorConfigModal })),
);
const RSConfigModal = lazy(() =>
  import('@/components/Chart/RSConfigModal').then((m) => ({ default: m.RSConfigModal })),
);
import { EPS_AXIS_WIDTH, resolveEpsColor as resolveEpsLineRawColor, resolveLossColor as resolveEpsLineLossColor } from '@/indicators/epsLine';
import { fmtShares } from '@/indicators/foreignHolding';
import { isIntradayResolution } from '@/types';
import { useMacroStore } from '@/store/macroStore';
import { MACRO_IND_PREFIX } from '@/indicators/macroOverlay';
import { Z_INDEX } from '@/constants/zIndex';

// klinecharts default line colours for indicator figures (same order the library uses)
const IND_LINE_COLORS = ['#FF9600', '#935EBD', '#2196F3', '#E11D74', '#01C5C4'];

type ChartV9 = Chart & {
  removeIndicator: (paneId: string, name?: string) => void;
  overrideIndicator: (o: { name: string; calcParams?: number[]; visible?: boolean; styles?: Record<string, unknown> }, paneId?: string) => void;
  subscribeAction: (type: string, cb: () => void) => void;
  unsubscribeAction: (type: string, cb: () => void) => void;
};

export function ChartLegend() {
  const symbol              = useChartStore((s) => s.symbol);
  const timeframe           = useChartStore((s) => s.timeframe);
  const watchlist           = useChartStore((s) => s.watchlist);
  const crosshair           = useChartStore((s) => s.crosshair);
  const lastBar             = useChartStore((s) => s.lastBar);
  const compareSymbols      = useChartStore((s) => s.compareSymbols);
  const compareAnchorTs     = useChartStore((s) => s.compareAnchorTs);
  const removeCompareSymbol = useChartStore((s) => s.removeCompareSymbol);
  const indicatorValues     = useChartStore((s) => s.indicatorValues);
  const indicatorPaneMap    = useChartStore((s) => s.indicatorPaneMap);
  const setIndicatorPaneMap = useChartStore((s) => s.setIndicatorPaneMap);
  const chart               = useChartStore((s) => s.chart) as ChartV9 | null;
  const rsLineConfigs       = useChartStore((s) => s.rsLineConfigs);
  const updateRSLine        = useChartStore((s) => s.updateRSLine);
  const removeRSLine        = useChartStore((s) => s.removeRSLine);
  const indicatorParams      = useChartStore((s) => s.indicatorParams);
  const indicatorColors      = useChartStore((s) => s.indicatorColors);
  const setIndicatorParam    = useChartStore((s) => s.setIndicatorParam);
  const clearIndicatorParam  = useChartStore((s) => s.clearIndicatorParam);
  const setIndicatorColors   = useChartStore((s) => s.setIndicatorColors);
  const clearIndicatorColors = useChartStore((s) => s.clearIndicatorColors);
  const indicatorScanConfigs     = useChartStore((s) => s.indicatorScanConfigs);
  const setIndicatorScanConfigs  = useChartStore((s) => s.setIndicatorScanConfigs);
  const volumeLabelEnabled       = useChartStore((s) => s.volumeLabelEnabled);
  const setVolumeLabelEnabled    = useChartStore((s) => s.setVolumeLabelEnabled);
  const crossVolumeEnabled       = useChartStore((s) => s.crossVolumeEnabled);
  const setCrossVolumeEnabled    = useChartStore((s) => s.setCrossVolumeEnabled);
  const rsiZonesEnabled          = useChartStore((s) => s.rsiZonesEnabled);
  const setRsiZonesEnabled       = useChartStore((s) => s.setRsiZonesEnabled);
  const rsiDivergenceEnabled     = useChartStore((s) => s.rsiDivergenceEnabled);
  const setRsiDivergenceEnabled  = useChartStore((s) => s.setRsiDivergenceEnabled);
  const rsiDivergencePeriod            = useChartStore((s) => s.rsiDivergencePeriod);
  const setRsiDivergencePeriod         = useChartStore((s) => s.setRsiDivergencePeriod);
  const rsiDivergencePivotLookback     = useChartStore((s) => s.rsiDivergencePivotLookback);
  const setRsiDivergencePivotLookback  = useChartStore((s) => s.setRsiDivergencePivotLookback);
  const setEpsLineEnabled        = useChartStore((s) => s.setEpsLineEnabled);
  const setCupHandleEnabled      = useChartStore((s) => s.setCupHandleEnabled);
  const setWilliamOneilSellEnabled = useChartStore((s) => s.setWilliamOneilSellEnabled);
  const isMobile = useIsMobile();

  const pinnedMacroSeries = useMacroStore((s) => s.pinnedSeries);
  const seriesMeta        = useMacroStore((s) => s.seriesMeta);

  const [hoveredInd, setHoveredInd]     = useState<string | null>(null);
  const [editingInd, setEditingInd]     = useState<string | null>(null);
  const [editParams, setEditParams]     = useState('');
  const hiddenInds                = useChartStore((s) => s.hiddenIndicators);
  const setHiddenInds             = useChartStore((s) => s.setHiddenIndicators);
  const [configuring, setConfiguring]   = useState<IndicatorDef | null>(null);
  const [rsModalOpen, setRsModalOpen]   = useState(false);
  const [epsLineModalOpen, setEpsLineModalOpen] = useState(false);
  const [cupHandleModalOpen, setCupHandleModalOpen] = useState(false);
  const [oneilSellModalOpen, setOneilSellModalOpen] = useState(false);
  const [foreignHoldingModalOpen, setForeignHoldingModalOpen] = useState(false);

  // Root div always renders so it's available for the ResizeObserver regardless of bar state.
  const rootRef = useRef<HTMLDivElement>(null);
  // Remembers whether volume labels were enabled before hiding the VOL pane,
  // so they can be restored when the pane is unhidden.
  const volLabelStateBeforeHideRef = useRef(false);

  // paneTopMap: paneId → pixel offset from the top of the chart container.
  // Sourced from chart.getPaneRect() — klinecharts' own layout engine.
  const [paneTopMap, setPaneTopMap] = useState<Map<string, number>>(new Map());
  // Height of the candle pane itself — used to cap the legend overlay so it can
  // never grow tall enough to hide the whole pane behind indicator/compare rows.
  const [candlePaneHeight, setCandlePaneHeight] = useState<number | null>(null);

  // Measures the actual rendered legend overlay (OHLCV + indicator/compare rows)
  // so its height is available to the rest of the app via the store, and so we
  // know when it has been clipped by legendMaxHeight below (to enable scrolling).
  const legendBoxRef = useRef<HTMLDivElement>(null);
  const [legendScrollable, setLegendScrollable] = useState(false);

  // Stable refs so the subscription callbacks always read current values.
  const chartRef            = useRef(chart);
  const indicatorPaneMapRef = useRef(indicatorPaneMap);
  useEffect(() => { chartRef.current = chart; },            [chart]);
  useEffect(() => { indicatorPaneMapRef.current = indicatorPaneMap; }, [indicatorPaneMap]);

  const refreshPaneTops = useCallback(() => {
    const c = chartRef.current;
    if (!c) return;
    const next = new Map<string, number>();
    // chart.getSize(paneId, DomPosition.Root) returns the pane's bounding rect
    // relative to the chart root — .top is pixels from the container top.
    const volSize = c.getSize('vol_pane', DomPosition.Root);
    if (volSize) next.set('vol_pane', volSize.top);
    indicatorPaneMapRef.current.forEach((paneId) => {
      if (paneId !== 'candle_pane' && paneId !== 'vol_pane') {
        const s = c.getSize(paneId, DomPosition.Root);
        if (s) next.set(paneId, s.top);
      }
    });
    setPaneTopMap(next);

    const candleSize = c.getSize('candle_pane', DomPosition.Root);
    setCandlePaneHeight(candleSize ? candleSize.height : null);
  }, []);

  // Refresh whenever chart instance or indicator map changes.
  useEffect(() => {
    // One frame delay — klinecharts needs a render cycle to create new panes.
    const id = requestAnimationFrame(refreshPaneTops);
    return () => cancelAnimationFrame(id);
  }, [chart, indicatorPaneMap, refreshPaneTops]);

  // Subscribe to pane-drag and scroll events so labels track live layout changes.
  useEffect(() => {
    const c = chart;
    if (!c) return;
    c.subscribeAction('onPaneDrag',          refreshPaneTops);
    c.subscribeAction('onVisibleRangeChange', refreshPaneTops);
    // Also watch for container resize (window resize, sidebar toggle, etc.)
    const el = rootRef.current;
    let ro: ResizeObserver | null = null;
    if (el) {
      ro = new ResizeObserver(refreshPaneTops);
      ro.observe(el);
    }
    return () => {
      c.unsubscribeAction('onPaneDrag',          refreshPaneTops);
      c.unsubscribeAction('onVisibleRangeChange', refreshPaneTops);
      ro?.disconnect();
    };
  }, [chart, refreshPaneTops]);

  // Register refreshPaneTops in the store so rebalancePanes can call it directly
  // after a programmatic resize() — without this, legend icons stay stale until
  // the user drags a pane divider or the ResizeObserver fires independently.
  useEffect(() => {
    useChartStore.getState().setRefreshPaneTops(refreshPaneTops);
    return () => { useChartStore.getState().setRefreshPaneTops(() => {}); };
  }, [refreshPaneTops]);

  const watchlistEntry = watchlist.find((w) => w.symbol === symbol);
  const symbolName = watchlistEntry?.name ?? '';
  const symbolLogo = watchlistEntry?.logo;
  const bar = crosshair ?? lastBar;

  // Report the legend overlay's actual rendered height to the store (consumed
  // elsewhere for layout decisions) and detect when legendMaxHeight below has
  // clipped it, so the box can become scrollable only when it's actually needed.
  useEffect(() => {
    const el = legendBoxRef.current;
    if (!el) { useChartStore.getState().setLegendHeight(0); return; }
    const measure = () => {
      useChartStore.getState().setLegendHeight(el.offsetHeight);
      setLegendScrollable(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      ro.disconnect();
      useChartStore.getState().setLegendHeight(0);
    };
    // Re-attach only when the overlay mounts/unmounts (bar null <-> non-null);
    // content-size changes within a mount are already caught by ResizeObserver.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bar !== null]);

  // Compute bar-derived values safely (bar may be null inside the always-rendered root div)
  const open       = bar?.open ?? null;
  const high       = bar?.high ?? null;
  const low        = bar?.low  ?? null;
  const close      = bar?.close ?? 0;
  const volume     = bar?.volume ?? null;
  const isUp       = open == null || close >= open;
  const change     = open != null ? close - open : null;
  const changePct  = change != null && open ? (change / open) * 100 : null;
  const color      = isUp ? 'var(--color-up)' : 'var(--color-down)';

  // Resolve live compare prices from the window maps populated by ChartContainer
  const compareTime = crosshair?.time ?? lastBar?.time ?? 0;
  function getCompareInfo(idx: number): { price: number | null; pct: number | null } {
    const map = (window as unknown as Record<string, unknown>)[`__compare_COMPARE_${idx}`] as Map<number, number> | undefined;
    if (!map) return { price: null, pct: null };
    const price = compareTime > 0 ? (map.get(compareTime) ?? null) : null;
    const anchorPrice = compareAnchorTs > 0 ? (map.get(compareAnchorTs) ?? null) : null;
    const pct = price != null && anchorPrice != null && anchorPrice !== 0
      ? (price - anchorPrice) / anchorPrice * 100
      : null;
    return { price, pct };
  }

  function fmt(n: number | null | undefined) {
    return n == null ? '—' : n.toFixed(2);
  }
  function fmtChange(n: number) {
    return (n >= 0 ? '+' : '') + n.toFixed(2);
  }
  function fmtPct(n: number) {
    return (n >= 0 ? '+' : '') + n.toFixed(2) + '%';
  }
  function fmtVol(n: number): string {
    if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2) + 'B';
    if (n >= 1_000_000)     return (n / 1_000_000).toFixed(2) + 'M';
    if (n >= 1_000)         return (n / 1_000).toFixed(1) + 'K';
    return n.toFixed(0);
  }
  // Compact formatter for large sub-pane indicator values (OBV, MACD histogram, etc.).
  // Handles negatives. Not used for main-pane price indicators.
  function fmtCompact(n: number): string {
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (abs >= 1_000_000_000) return sign + (abs / 1_000_000_000).toFixed(2) + 'B';
    if (abs >= 1_000_000)     return sign + (abs / 1_000_000).toFixed(2) + 'M';
    if (abs >= 1_000)         return sign + (abs / 1_000).toFixed(1) + 'K';
    return n.toFixed(0);
  }

  function removeIndicator(name: string) {
    const paneId = indicatorPaneMap.get(name);
    if (!paneId || !chart) return;
    chart.removeIndicator(paneId, name);
    const next = new Map(indicatorPaneMap);
    next.delete(name);
    // When removing MOMENTUM_OVERLAY also remove the paired tracker sub-pane
    if (name === 'MOMENTUM_OVERLAY') {
      const trackerPaneId = indicatorPaneMap.get('MOMENTUM_TRACKER');
      if (trackerPaneId) chart.removeIndicator(trackerPaneId, 'MOMENTUM_TRACKER');
      next.delete('MOMENTUM_TRACKER');
      clearIndicatorParam('MOMENTUM_TRACKER');
      clearIndicatorColors('MOMENTUM_TRACKER');
    }
    // When removing VOL, also remove its companion stacked indicators
    // so the vol_pane is fully destroyed and no ghost bars remain.
    if (name === 'VOL') {
      try { chart.removeIndicator(paneId, 'VOL_LABELS'); } catch { /* ignore */ }
      try { chart.removeIndicator(paneId, 'CROSS_VOL'); } catch { /* ignore */ }
      if (volumeLabelEnabled) setVolumeLabelEnabled(false);
    }
    // RSI's zone/divergence companions are removed along with RSI; their
    // enabled flags are intentionally left untouched (see IndicatorModal.tsx).
    if (name === 'RSI') {
      try { chart.removeIndicator(paneId, 'RSI_ZONES'); } catch { /* ignore */ }
      try { chart.removeIndicator(paneId, 'RSI_DIVERGENCE'); } catch { /* ignore */ }
    }
    setIndicatorPaneMap(next);
    setHoveredInd(null);
    setEditingInd(null);
    if (name === 'EPS_LINE') setEpsLineEnabled(false);
    if (name === 'CUP_HANDLE') setCupHandleEnabled(false);
    if (name === 'WILLIAM_ONEIL_SELL') setWilliamOneilSellEnabled(false);
    // Logo position depends on pricePaneHeight — refresh it after klinecharts
    // collapses the removed pane and redistributes heights.
    requestAnimationFrame(() => useChartStore.getState().refreshPricePaneHeight());
  }

  function toggleVisibility(name: string) {
    const paneId = indicatorPaneMap.get(name);
    if (!paneId || !chart) return;
    const nowHidden = !hiddenInds.has(name);
    // Update the store first: rebalancePanes reads it to size collapsed panes.
    const nextHidden = new Set(hiddenInds);
    if (nowHidden) { nextHidden.add(name); } else { nextHidden.delete(name); }
    setHiddenInds(nextHidden);
    try {
      chart.overrideIndicator({ name, visible: !nowHidden }, paneId);
    } catch { /* ignore */ }
    if (paneId !== 'candle_pane') {
      if (name === 'VOL') {
        // rebalancePanes doesn't manage the vol pane, so size it directly.
        try {
          chart.setPaneOptions({ id: paneId, height: nowHidden ? 28 : 80, minHeight: nowHidden ? 24 : 60 });
        } catch { /* ignore */ }
        requestAnimationFrame(refreshPaneTops);
        requestAnimationFrame(() => useChartStore.getState().refreshPricePaneHeight());
      } else {
        void useChartStore.getState().rebalancePanes();
      }
    }
    if (name === 'VOL' && nowHidden) {
      // Also hide companion indicators so crossing bars don't remain visible
      try { chart.overrideIndicator({ name: 'CROSS_VOL', visible: false }, 'vol_pane'); } catch { /* ignore */ }
      try { chart.overrideIndicator({ name: 'VOL_LABELS', visible: false }, 'vol_pane'); } catch { /* ignore */ }
      if (volumeLabelEnabled) {
        volLabelStateBeforeHideRef.current = true;
        setVolumeLabelEnabled(false);
      }
    } else if (name === 'VOL' && !nowHidden) {
      // Restore companion indicators when VOL is shown again
      try { chart.overrideIndicator({ name: 'CROSS_VOL', visible: true }, 'vol_pane'); } catch { /* ignore */ }
      try { chart.overrideIndicator({ name: 'VOL_LABELS', visible: true }, 'vol_pane'); } catch { /* ignore */ }
      if (volLabelStateBeforeHideRef.current) {
        volLabelStateBeforeHideRef.current = false;
        setVolumeLabelEnabled(true);
      }
    }
    if (name === 'RSI') {
      // Keep RSI's zone/divergence companions in sync with the RSI pane's own visibility.
      try { chart.overrideIndicator({ name: 'RSI_ZONES', visible: !nowHidden }, paneId); } catch { /* ignore */ }
      try { chart.overrideIndicator({ name: 'RSI_DIVERGENCE', visible: !nowHidden }, paneId); } catch { /* ignore */ }
    }
  }

  // ── Mobile sub-pane resize ────────────────────────────────────────────────
  // klinecharts' own divider drag only has a 7px hit area — unusable with a finger —
  // so mobile gets a dedicated grab handle on each open sub-pane's top edge. Dragging
  // it sets the pane's pixel height; the price pane absorbs the difference.
  const resizeRef = useRef<{ paneId: string; startY: number; startH: number; maxH: number } | null>(null);

  function handleResizeStart(e: React.PointerEvent<HTMLDivElement>, paneId: string) {
    const c = chartRef.current;
    if (!c) return;
    const pane = c.getSize(paneId, DomPosition.Root);
    const candle = c.getSize('candle_pane', DomPosition.Root);
    if (!pane || !candle) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    resizeRef.current = {
      paneId,
      startY: e.clientY,
      startH: pane.height,
      maxH: Math.max(SUBPANE_MIN_HEIGHT, pane.height + candle.height - CANDLE_MIN_HEIGHT),
    };
  }

  function handleResizeMove(e: React.PointerEvent<HTMLDivElement>) {
    const r = resizeRef.current;
    const c = chartRef.current;
    if (!r || !c) return;
    const height = Math.round(Math.min(r.maxH, Math.max(SUBPANE_MIN_HEIGHT, r.startH - (e.clientY - r.startY))));
    try { c.setPaneOptions({ id: r.paneId, height, minHeight: SUBPANE_MIN_HEIGHT }); } catch { return; }
    useChartStore.getState().setPaneHeight(r.paneId, height);
    // setPaneOptions doesn't emit the drag action that legend rows, axis badges and
    // the price-pane logo listen to — fire it so they follow the new layout.
    try {
      (c as unknown as { getChartStore: () => { getActionStore: () => { execute: (t: string, d: unknown) => void } } })
        .getChartStore().getActionStore().execute('onPaneDrag', { paneId: r.paneId });
    } catch { /* ignore */ }
  }

  function handleResizeEnd() {
    resizeRef.current = null;
  }

  // Sub-panes that start collapsed (mobile default, set in chartStore) are only
  // *flagged* hidden in the store — klinecharts still has to be told to stop
  // drawing them. Tracked in a ref so each pane is only overridden once.
  const appliedHiddenRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!chart) return;
    const applied = appliedHiddenRef.current;
    applied.forEach((n) => { if (!hiddenInds.has(n) || !indicatorPaneMap.has(n)) applied.delete(n); });
    hiddenInds.forEach((name) => {
      const paneId = indicatorPaneMap.get(name);
      if (!paneId || paneId === 'candle_pane' || applied.has(name)) return;
      try { chart.overrideIndicator({ name, visible: false }, paneId); } catch { /* ignore */ }
      if (name === 'RSI') {
        try { chart.overrideIndicator({ name: 'RSI_ZONES', visible: false }, paneId); } catch { /* ignore */ }
        try { chart.overrideIndicator({ name: 'RSI_DIVERGENCE', visible: false }, paneId); } catch { /* ignore */ }
      }
      applied.add(name);
    });
  }, [chart, hiddenInds, indicatorPaneMap]);

  function applyEdit(name: string) {
    const paneId = indicatorPaneMap.get(name);
    if (!paneId || !chart) return;
    const nums = editParams.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n) && n > 0);
    if (!nums.length) return;
    chart.overrideIndicator({ name, calcParams: nums }, paneId);
    setEditingInd(null);
  }

  function handleLegendUpdate(ind: IndicatorDef, params: number[], colors: string[]) {
    if (!chart) return;
    const paneId = indicatorPaneMap.get(ind.name);
    if (!paneId) return;
    const override: { name: string; calcParams?: number[]; styles?: Record<string, unknown> } = { name: ind.name };
    if (params.length > 0) override.calcParams = params;
    if (ind.name === 'VOL') {
      const maColors = colors.slice(2);
      override.styles = {
        bars: [{
          style: 'fill',
          upColor:       colors[0],
          downColor:     colors[1] ?? colors[0],
          noChangeColor: colors[0],
        }],
        ...(maColors.length ? { lines: maColors.map((color) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })) } : {}),
      };
    } else {
      // Must include ALL line style fields — KlineCharts' merge-loop checks dashedValue[0] without
      // a null guard, so a bare { color } object causes "Cannot read properties of undefined (reading '0')".
      override.styles = { lines: colors.map((color) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })) };
    }
    chart.overrideIndicator(override, paneId);
    if (params.length > 0) setIndicatorParam(ind.name, params);
    setIndicatorColors(ind.name, colors);
    setConfiguring(null);
  }

  function handleLegendPreviewUpdate(ind: IndicatorDef, params: number[], colors: string[]) {
    if (!chart) return;
    const paneId = indicatorPaneMap.get(ind.name);
    if (!paneId) return;
    const override: { name: string; calcParams?: number[]; styles?: Record<string, unknown> } = { name: ind.name };
    if (params.length > 0) override.calcParams = params;
    if (ind.name === 'VOL') {
      const maColors = colors.slice(2);
      override.styles = {
        bars: [{
          style: 'fill',
          upColor:       colors[0],
          downColor:     colors[1] ?? colors[0],
          noChangeColor: colors[0],
        }],
        ...(maColors.length ? { lines: maColors.map((color) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })) } : {}),
      };
    } else {
      override.styles = { lines: colors.map((color) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })) };
    }
    chart.overrideIndicator(override, paneId);
    if (params.length > 0) setIndicatorParam(ind.name, params);
    setIndicatorColors(ind.name, colors);
  }

  function handleLegendRemove(ind: IndicatorDef) {
    removeIndicator(ind.name);
    clearIndicatorParam(ind.name);
    clearIndicatorColors(ind.name);
    setConfiguring(null);
  }

  // ── Right-axis value labels for each pane ─────────────────────────────────
  // Shows current indicator figure values at the right edge of each pane,
  // color-coded by line. Values follow the rightmost visible bar.
  function renderPaneAxisValues(paneId: string, paneTop: number) {
    if (!isMobile) return null;

    const paneInds = [...indicatorPaneMap.entries()]
      .filter(([, pid]) => pid === paneId)
      .map(([name]) => name)
      .filter((name) => {
        if (name.startsWith(MACRO_IND_PREFIX)) {
          const seriesId = name.slice(MACRO_IND_PREFIX.length);
          const pinned = pinnedMacroSeries.find((s) => s.seriesId === seriesId);
          return pinned?.visible ?? false;
        }
        return !hiddenInds.has(name);
      });

    const labels = paneInds.flatMap((name) => {
      const vals = indicatorValues[name];
      if (!vals) return [];
      let colors: string[];
      if (name.startsWith(MACRO_IND_PREFIX)) {
        const seriesId = name.slice(MACRO_IND_PREFIX.length);
        const pinned = pinnedMacroSeries.find((s) => s.seriesId === seriesId);
        colors = pinned ? [pinned.color] : [];
      } else {
        colors = indicatorColors[name] ?? INDICATOR_DEFAULT_COLORS[name] ?? [];
      }
      const isVol = name === 'VOL';
      return vals.entries.map((entry, fi) => {
        // Mirror renderIndRow's compact formatting: VOL entries (raw bar + MA
        // lines) always go through fmtVol; other sub-pane indicators (OBV,
        // MACD histogram, etc.) get fmtCompact once they hit 1,000. Candle-pane
        // (price) values stay full precision, same as the desktop legend.
        let displayValue = entry.value;
        if (isVol) {
          const rawNum = parseFloat(entry.value);
          if (!isNaN(rawNum)) displayValue = fmtVol(rawNum);
        } else if (paneId !== 'candle_pane' && !entry.value.includes('%') && entry.value !== '—') {
          const parsed = parseFloat(entry.value);
          if (!isNaN(parsed) && Math.abs(parsed) >= 1_000) displayValue = fmtCompact(parsed);
        }
        return {
          key: `${name}_${entry.title}`,
          title: entry.title,
          value: displayValue,
          color: colors[fi] ?? IND_LINE_COLORS[fi % IND_LINE_COLORS.length],
        };
      });
    });

    if (labels.length === 0) return null;

    return (
      <div
        key={`axis-${paneId}`}
        style={{
          position: 'absolute',
          top: paneTop + 4,
          right: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '1px',
          pointerEvents: 'none',
          zIndex: Z_INDEX.chartOverlay + 1,
        }}
      >
        {labels.map((label) => (
          <span
            key={label.key}
            style={{
              fontSize: isMobile ? 10 : 11,
              fontFamily: 'monospace',
              color: label.color,
              background: 'var(--glass-header)',
              padding: '1px 4px',
              borderRadius: 2,
              whiteSpace: 'nowrap',
              lineHeight: 1.3,
              backdropFilter: 'blur(2px)',
            }}
          >
            {label.title}{label.value}
          </span>
        ))}
      </div>
    );
  }

  // Separate indicators by pane location
  const mainPaneInds = INDICATORS.filter(
    (def) => indicatorPaneMap.has(def.name) && def.paneId === 'candle_pane' && !def.hiddenFromLegend
  );
  const volActive = indicatorPaneMap.has('VOL');
  // User-created sub-pane inds (paneId===undefined in defs), ordered by Map insertion order
  const userSubPaneInds = ([...indicatorPaneMap.keys()] as string[])
    .filter((name) => {
      const def = INDICATORS.find((i) => i.name === name);
      return def && !def.paneId && !def.hiddenFromLegend;
    })
    .map((name) => INDICATORS.find((i) => i.name === name)!);

  // Macro sub-pane indicator names (dynamically registered, not in INDICATORS)
  const macroSubPaneNames = ([...indicatorPaneMap.keys()] as string[])
    .filter((name) => name.startsWith(MACRO_IND_PREFIX));

  // Shared row renderer.
  // isSubPane=true: static action buttons pinned top-right; labels at top-left.
  // VOL indicator: MA values shown in K/M/B + % deviation of current volume from each MA.
  function renderIndRow(def: (typeof INDICATORS)[number], isSubPane = false) {
    const vals      = indicatorValues[def.name];
    const isHovered = hoveredInd === def.name;
    const isHidden  = hiddenInds.has(def.name);
    // Show the gear whenever the indicator has anything configurable (calc params
    // or output colours) — mirrors IndicatorBar's toolbar pill, which always shows
    // the gear for an active indicator. Previously gated on `defaultParams`, which
    // hid the gear for zero-param built-ins (PVT, OBV, AO, SAR, PER_LINES,
    // MA_BREADTH, WEEK52_HLN) even though their colours are still editable via
    // IndicatorConfigModal. `showSettingsGear: false` (e.g. CROSS_VOL) still opts out.
    const showGear = def.showSettingsGear !== false;
    const isVol          = def.name === 'VOL';
    const isSpikeStrip   = def.name === 'VOL_SPIKE_STRIP';
    const isSpikeTimeline = def.name === 'VOL_SPIKE_TIMELINE';
    const isSpike        = isSpikeStrip || isSpikeTimeline;
    const isEpsLine      = def.name === 'EPS_LINE';
    const isForeignHolding = def.name === 'FOREIGN_HOLDING';

    const BTN = { background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, fontSize: '16px', padding: '0 2px', opacity: 0.7 } as const;

    const actionButtons = (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', pointerEvents: 'auto' }}>
        <button
          title={isHidden ? 'Show' : 'Hide'}
          onClick={() => toggleVisibility(def.name)}
          style={{ ...BTN, color: isHidden ? 'var(--color-muted)' : 'var(--color-text)' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
        >{isHidden ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}</button>
        {showGear && (
          <button
            title="Settings"
            onClick={() => def.name === 'EPS_LINE' ? setEpsLineModalOpen(true) : def.name === 'CUP_HANDLE' ? setCupHandleModalOpen(true) : def.name === 'WILLIAM_ONEIL_SELL' ? setOneilSellModalOpen(true) : def.name === 'FOREIGN_HOLDING' ? setForeignHoldingModalOpen(true) : setConfiguring(def)}
            style={{ ...BTN, color: 'var(--color-text)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
          >⚙</button>
        )}
        <button
          title={`Remove ${def.name}`}
          onClick={() => removeIndicator(def.name)}
          style={{ ...BTN, color: 'var(--color-muted)' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
        ><XIcon size={16} /></button>
      </span>
    );

    // Mobile sub-panes start collapsed to just this title; tapping it expands/collapses the pane.
    const tapToToggle = isMobile && isSubPane;

    const labelContent = (
      <>
        <span
          role={tapToToggle ? 'button' : undefined}
          aria-expanded={tapToToggle ? !isHidden : undefined}
          onClick={tapToToggle ? () => toggleVisibility(def.name) : undefined}
          style={{
            fontSize: '13px', fontFamily: 'monospace', color: 'var(--color-muted)', lineHeight: 1.3,
            opacity: isHidden && !tapToToggle ? 0.4 : 1,
            ...(tapToToggle ? { cursor: 'pointer', padding: '4px 8px 4px 0', userSelect: 'none' } : {}),
          }}
        >
          {tapToToggle && <span aria-hidden="true">{isHidden ? '▸' : '▾'} </span>}
          {isEpsLine ? 'EPS Line' : def.name}{vals?.params ?? ''}
        </span>

        {/* ── VOL_SPIKE_STRIP: custom crosshair legend (vol/ma/pct from figures) ── */}
        {isSpikeStrip && (!isHidden || !isSubPane) && vals && (() => {
          const maPeriod = vals.params.replace(/[()]/g, '').trim() || '20';
          const labelColor = indicatorColors['VOL_SPIKE_STRIP']?.[0] ?? '#60A5FA';
          const volEntry = vals.entries.find((e) => e.title === 'Vol: ');
          const maEntry  = vals.entries.find((e) => e.title === 'MA: ');
          const pctEntry = vals.entries.find((e) => e.title === '\u0394%: ');
          const volNum = volEntry && volEntry.value !== '\u2014' ? parseFloat(volEntry.value) : null;
          const maNum  = maEntry  && maEntry.value  !== '\u2014' ? parseFloat(maEntry.value)  : null;
          const pctNum = pctEntry && pctEntry.value !== '\u2014' ? parseFloat(pctEntry.value) : null;
          if (volNum == null && maNum == null) return null;
          return (
            <span key="spike-vals" style={{ fontSize: '13px', fontFamily: 'monospace', color: labelColor, lineHeight: 1.3, opacity: isHidden ? 0.4 : 1 }}>
              {maNum  != null && `MA${maPeriod} Avg: ${fmtVol(maNum)}`}
              {volNum != null && `  Vol: ${fmtVol(volNum)}`}
              {pctNum != null && (
                <span style={{ opacity: 0.85 }}>{`  \u0394: ${pctNum >= 0 ? '+' : ''}${pctNum.toFixed(1)}%`}</span>
              )}
            </span>
          );
        })()}

        {/* ── VOL_SPIKE_TIMELINE: vol from bar data, ma derived from vol/(1+pct/100) ── */}
        {isSpikeTimeline && (!isHidden || !isSubPane) && vals && (() => {
          const maPeriod = vals.params.replace(/[()]/g, '').trim() || '20';
          const labelColor = indicatorColors['VOL_SPIKE_TIMELINE']?.[0] ?? '#60A5FA';
          const pctEntry = vals.entries.find((e) => e.title === '\u0394%: ');
          const pctNum = pctEntry && pctEntry.value !== '\u2014' ? parseFloat(pctEntry.value) : null;
          const volNum = volume;
          const maNum  = volNum != null && pctNum != null ? volNum / (1 + pctNum / 100) : null;
          if (volNum == null) return null;
          return (
            <span key="spike-tl-vals" style={{ fontSize: '13px', fontFamily: 'monospace', color: labelColor, lineHeight: 1.3, opacity: isHidden ? 0.4 : 1 }}>
              {maNum  != null && `MA${maPeriod} Avg: ${fmtVol(maNum)}`}
              {`  Vol: ${fmtVol(volNum)}`}
              {pctNum != null && (
                <span style={{ opacity: 0.85 }}>{`  \u0394: ${pctNum >= 0 ? '+' : ''}${pctNum.toFixed(1)}%`}</span>
              )}
            </span>
          );
        })()}
        {/* ── EPS_LINE: crosshair-driven EPS data from window.__eps_line_data ──────────── */}
        {isEpsLine && !isHidden && (() => {
          const epsData = window.__eps_line_data;
          const cursorTime = crosshair?.time ?? lastBar?.time ?? null;
          let cqGrowth: number | null = epsData?.currentQuarterGrowth ?? null;
          let cqRevGrowth: number | null = epsData?.currentQuarterRevenueGrowth ?? null;
          let cqEps: number | null = epsData?.currentQuarterEps ?? null;
          let ttmEps: number | null = epsData?.ttmEps ?? null;
          let cqDateStr = '';
          if (cursorTime != null && epsData?.points?.length) {
            const atOrBefore = epsData.points.filter((p) => p.time <= cursorTime);
            if (atOrBefore.length > 0) {
              const latest = atOrBefore[atOrBefore.length - 1];
              cqGrowth = latest.growth;
              cqRevGrowth = latest.revenueGrowth;
              cqEps = latest.eps;
              const last4 = atOrBefore.slice(-4);
              ttmEps = last4.reduce((acc, p) => acc + p.eps, 0);
              const d = new Date(latest.time * 1000);
              cqDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            }
          }
          const isNeg = cqEps != null && cqEps < 0;
          const epsColor = isNeg ? resolveEpsLineLossColor() : resolveEpsLineRawColor();
          const growthStr = cqGrowth != null
            ? `${cqGrowth >= 0 ? '+' : ''}${(cqGrowth * 100).toFixed(0)}%` : null;
          const revGrowthStr = cqRevGrowth != null
            ? `${cqRevGrowth >= 0 ? '+' : ''}${(cqRevGrowth * 100).toFixed(0)}%` : null;
          return (
            <>
              {cqEps != null && (
                <span key="eps-val" style={{ fontSize: '13px', fontFamily: 'monospace', color: epsColor, lineHeight: 1.3 }}>
                  EPS: {cqEps.toFixed(2)}{cqDateStr ? ` (${cqDateStr})` : ''}
                </span>
              )}
              {growthStr && (
                <span key="eps-yoy" style={{ fontSize: '13px', fontFamily: 'monospace', color: epsColor, lineHeight: 1.3 }}>
                  YoY: {growthStr}
                </span>
              )}
              {revGrowthStr && (
                <span key="eps-rev" style={{ fontSize: '13px', fontFamily: 'monospace', color: '#60A5FA', lineHeight: 1.3 }}>
                  Rev: {revGrowthStr}
                </span>
              )}
              {ttmEps != null && (
                <span key="eps-ttm" style={{ fontSize: '13px', fontFamily: 'monospace', color: epsColor, lineHeight: 1.3, opacity: 0.75 }}>
                  TTM: {ttmEps.toFixed(2)}
                </span>
              )}
              {epsData?.nextQuarterEps?.value != null && (
                <span key="eps-est" style={{ fontSize: '13px', fontFamily: 'monospace', color: epsColor, lineHeight: 1.3, opacity: 0.75 }}>
                  Est: {epsData.nextQuarterEps.value.toFixed(2)}{epsData.nextQuarterEps.growthRate != null ? ` (${epsData.nextQuarterEps.growthRate >= 0 ? '+' : ''}${(epsData.nextQuarterEps.growthRate * 100).toFixed(0)}%)` : ''}
                </span>
              )}
            </>
          );
        })()}
        {/* ── FOREIGN_HOLDING: crosshair-driven data from window.__foreign_holding_data ── */}
        {isForeignHolding && !isHidden && (() => {
          const fhData = window.__foreign_holding_data;
          const cursorTime = crosshair?.time ?? lastBar?.time ?? null;
          if (!fhData?.points?.length) return null;
          const atOrBefore = cursorTime != null
            ? fhData.points.filter((p) => p.time <= cursorTime)
            : fhData.points;
          if (atOrBefore.length === 0) return null;
          const latest = atOrBefore[atOrBefore.length - 1];

          return (
            <>
              {latest.pct != null && (
                <span key="fgn-pct" style={{ fontSize: '13px', fontFamily: 'monospace', color: 'var(--color-text)', lineHeight: 1.3 }}>
                  Foreign: {latest.pct.toFixed(2)}%
                </span>
              )}
              <span key="fgn-abs" style={{ fontSize: '13px', fontFamily: 'monospace', color: 'var(--color-muted)', lineHeight: 1.3 }}>
                Absolute: {fmtShares(latest.foreignHolding)}
              </span>
              {latest.delta != null && (
                <span
                  key="fgn-delta"
                  style={{
                    fontSize: '13px', fontFamily: 'monospace', lineHeight: 1.3,
                    color: latest.delta >= 0 ? useChartStore.getState().foreignHoldingUpColor : useChartStore.getState().foreignHoldingDownColor,
                  }}
                >
                  Net Δ: {latest.delta >= 0 ? '+' : ''}{fmtShares(latest.delta)}
                </span>
              )}
              {latest.deltaPct != null && (
                <span
                  key="fgn-deltapct"
                  style={{
                    fontSize: '13px', fontFamily: 'monospace', lineHeight: 1.3,
                    color: latest.deltaPct >= 0 ? useChartStore.getState().foreignHoldingUpColor : useChartStore.getState().foreignHoldingDownColor,
                  }}
                >
                  Δ%: {latest.deltaPct >= 0 ? '+' : ''}{latest.deltaPct.toFixed(2)}%
                </span>
              )}
              {latest.divergence && (() => {
                const store = useChartStore.getState();
                const divColors: Record<string, string> = {
                  'confirm-bull': store.foreignHoldingConfirmBullColor,
                  distribution: store.foreignHoldingDistributionColor,
                  accumulation: store.foreignHoldingAccumulationColor,
                  'confirm-bear': store.foreignHoldingConfirmBearColor,
                };
                const divLabel: Record<string, string> = {
                  'confirm-bull': 'Confirms (bull)',
                  distribution: 'Distribution warning',
                  accumulation: 'Accumulation warning',
                  'confirm-bear': 'Confirms (bear)',
                };
                return (
                  <span key="fgn-div" style={{ fontSize: '13px', fontFamily: 'monospace', color: divColors[latest.divergence!], lineHeight: 1.3, opacity: 0.9 }}>
                    {divLabel[latest.divergence!]}
                  </span>
                );
              })()}
            </>
          );
        })()}
        {/* ── Default entries for all other indicators ─────────────────── */}
        {/* On mobile, sub-pane numeric values are shown via the right-axis labels
            instead (see renderPaneAxisValues) — skip them here to avoid duplication,
            but keep the row itself so action buttons and custom legends still render. */}
        {!isSpike && (!isHidden || !isSubPane) && !(isMobile && isSubPane) && vals?.entries.map((entry, fi) => {
          // For VOL indicator: fi=0 is the raw volume bar (title "VOL:"), rest are MA lines
          const isRawVolBar = isVol && !entry.title.toUpperCase().startsWith('MA');
          const maIndex = isVol ? fi - 1 : fi;
          // Match the actual drawn line colour (user override, else the app default) —
          // same precedence renderPaneAxisValues already uses. VOL's colour array is laid
          // out [upColor, downColor, ...maColors] (see handleLegendUpdate), so its MA
          // entries read from an offset of 2. IND_LINE_COLORS is only the last-resort
          // fallback when nothing is configured, not the primary source.
          const appliedColors = indicatorColors[def.name] ?? INDICATOR_DEFAULT_COLORS[def.name] ?? [];
          const lineColor = isRawVolBar
            ? color
            : isVol
              ? (appliedColors[2 + maIndex] ?? IND_LINE_COLORS[maIndex % IND_LINE_COLORS.length])
              : (appliedColors[fi] ?? IND_LINE_COLORS[fi % IND_LINE_COLORS.length]);
          // "—" (no value yet, e.g. an MA longer than the bars available) parses to
          // NaN — treat that as "no number" so it isn't fed to fmtVol, which
          // would print the literal text "NaN".
          const parsedVol = isVol ? parseFloat(entry.value) : NaN;
          const rawNum = Number.isFinite(parsedVol) ? parsedVol : null;
          const maVal = isVol && !isRawVolBar && rawNum != null && !isNaN(rawNum) ? rawNum : null;
          const devPct = maVal != null && maVal > 0 && volume != null
            ? ((volume - maVal) / maVal) * 100
            : def.paneId === 'candle_pane' && close != null
              ? (() => { const v = parseFloat(entry.value); return !isNaN(v) && v > 0 ? ((close - v) / v) * 100 : null; })()
              : null;
          let displayVal: string;
          if (isVol && rawNum != null) {
            displayVal = fmtVol(rawNum);
          } else if (isSubPane && !entry.value.includes('%') && entry.value !== '—') {
            // Compact-format large sub-pane numbers (OBV, MACD histogram, CCI, ATR, etc.).
            // Main-pane indicators show full price values — skip them (isSubPane === false).
            const parsed = parseFloat(entry.value);
            displayVal = !isNaN(parsed) && Math.abs(parsed) >= 1_000 ? fmtCompact(parsed) : entry.value;
          } else {
            displayVal = entry.value;
          }
          return (
            <span key={entry.title} style={{ fontSize: '13px', fontFamily: 'monospace', color: lineColor, lineHeight: 1.3, opacity: isHidden ? 0.4 : 1 }}>
              {entry.title}{displayVal}
              {devPct != null && (
                <span style={{ opacity: 0.8 }}> ({devPct >= 0 ? '+' : ''}{devPct.toFixed(1)}%)</span>
              )}
            </span>
          );
        })}
      </>
    );

    const paramEditor = editingInd === def.name && (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', pointerEvents: 'auto' }}>
        <input
          autoFocus
          value={editParams}
          onChange={(e) => setEditParams(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applyEdit(def.name); if (e.key === 'Escape') setEditingInd(null); }}
          style={{ fontSize: '13px', fontFamily: 'monospace', background: 'var(--color-panel, #1e222d)', border: '1px solid var(--color-border, #363a45)', borderRadius: '3px', color: 'var(--color-text)', padding: '1px 5px', width: '90px', outline: 'none' }}
        />
        <button
          onClick={() => applyEdit(def.name)}
          style={{ fontSize: '12px', padding: '1px 6px', background: 'var(--color-accent, #2962ff)', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
        >OK</button>
      </span>
    );

    if (isSubPane) {
      // Sub-pane: labels top-left, actions pinned to top-right.
      // Mobile: always visible. Desktop: visible only on hover.
      return (
        <div
          key={def.name}
          style={{ position: 'relative', pointerEvents: 'auto', minHeight: '28px' }}
          onMouseEnter={() => setHoveredInd(def.name)}
          onMouseLeave={() => { setHoveredInd(null); setEditingInd(null); }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap', padding: isMobile ? '4px 56px 4px 10px' : '4px 120px 4px 10px' }}>
            {labelContent}
            {paramEditor}
          </div>
          <div style={{
            position: 'absolute', top: '4px', right: '64px',
            display: 'flex', alignItems: 'center', gap: '2px',
            opacity: (isHovered || isMobile) ? 1 : 0,
            pointerEvents: (isHovered || isMobile) ? 'auto' : 'none',
            transition: 'opacity 150ms ease',
          }}>
            {actionButtons}
          </div>
        </div>
      );
    }

    // Main-pane: inline, actions always rendered (preserves row height) but hidden when not hovered
    // Mobile: values are shown on the right price axis via klinecharts lastValueMark;
    // legend shows only the indicator name + action buttons.
    const mobileNameOnly = isMobile && (
      <span style={{ fontSize: '12px', fontFamily: 'monospace', color: 'var(--color-muted)', lineHeight: 1.3 }}>
        {def.name}{vals?.params ?? ''}
      </span>
    );

    return (
      <div
        key={def.name}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '1px', pointerEvents: 'auto' }}
        onMouseEnter={() => setHoveredInd(def.name)}
        onMouseLeave={() => { setHoveredInd(null); setEditingInd(null); }}
      >
        {isMobile ? mobileNameOnly : labelContent}
        <span style={{
          opacity: (isHovered || isMobile) ? 1 : 0,
          transform: (isHovered || isMobile) ? 'translateX(0)' : 'translateX(-6px)',
          transition: 'opacity 150ms ease, transform 150ms ease',
          pointerEvents: (isHovered || isMobile) ? 'auto' : 'none',
        }}>
          {actionButtons}
        </span>
        {paramEditor}
      </div>
    );
  }

  // ── Macro indicator row renderer ──────────────────────────────────────────
  function renderMacroIndRow(indName: string) {
    const seriesId = indName.slice(MACRO_IND_PREFIX.length);
    const meta = seriesMeta.find((m) => m.series_id === seriesId);
    const pinned = pinnedMacroSeries.find((s) => s.seriesId === seriesId);
    const isHidden = !pinned?.visible;
    const vals = indicatorValues[indName];
    const isHovered = hoveredInd === indName;
    const displayName = meta?.name ?? seriesId;

    const BTN = { background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, fontSize: '16px', padding: '0 2px', opacity: 0.7 } as const;

    const actionButtons = (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', pointerEvents: 'auto' }}>
        <button
          title={isHidden ? 'Show' : 'Hide'}
          onClick={() => {
            trackMacroVisibility(seriesId, isHidden);
            useMacroStore.getState().toggleVisibility(seriesId);
          }}
          style={{ ...BTN, color: isHidden ? 'var(--color-muted)' : 'var(--color-text)' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
        >{isHidden ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}</button>
        <button
          title={`Remove ${displayName}`}
          onClick={() => {
            trackMacroAction('remove', seriesId);
            useMacroStore.getState().removeSeries(seriesId);
          }}
          style={{ ...BTN, color: 'var(--color-muted)' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
        ><XIcon size={16} /></button>
      </span>
    );

    return (
      <div
        key={indName}
        style={{ position: 'relative', pointerEvents: 'auto', minHeight: '28px' }}
        onMouseEnter={() => setHoveredInd(indName)}
        onMouseLeave={() => { setHoveredInd(null); setEditingInd(null); }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap', padding: isMobile ? '4px 56px 4px 10px' : '4px 120px 4px 10px' }}>
          <span style={{ fontSize: '13px', fontFamily: 'monospace', color: 'var(--color-muted)', lineHeight: 1.3, opacity: isHidden ? 0.4 : 1 }}>
            {displayName}
          </span>
          {!isHidden && !isMobile && vals?.entries.map((entry, fi) => {
            const lineColor = pinned?.color ?? IND_LINE_COLORS[fi % IND_LINE_COLORS.length];
            return (
              <span key={entry.title} style={{ fontSize: '13px', fontFamily: 'monospace', color: lineColor, lineHeight: 1.3 }}>
                {entry.title}{entry.value}
              </span>
            );
          })}
        </div>
        <div style={{
          position: 'absolute', top: '4px', right: '64px',
          display: 'flex', alignItems: 'center', gap: '2px',
          opacity: (isHovered || isMobile) ? 1 : 0,
          pointerEvents: (isHovered || isMobile) ? 'auto' : 'none',
          transition: 'opacity 150ms ease',
        }}>
          {actionButtons}
        </div>
      </div>
    );
  }

  // One grab handle per open (expanded) sub-pane, centred on its top edge (mobile only).
  const resizeHandleNames = isMobile ? [
    ...userSubPaneInds.filter((def) => !hiddenInds.has(def.name)).map((def) => def.name),
    ...macroSubPaneNames.filter((n) => pinnedMacroSeries.find((s) => s.seriesId === n.slice(MACRO_IND_PREFIX.length))?.visible),
  ] : [];
  const resizeHandles = resizeHandleNames.map((name) => {
    const paneId = indicatorPaneMap.get(name);
    const top    = paneId ? paneTopMap.get(paneId) : undefined;
    if (!paneId || top == null) return null;
    return (
      <div
        key={`resize-${paneId}`}
        role="separator"
        aria-orientation="horizontal"
        aria-label={`Resize ${name} pane`}
        onPointerDown={(e) => handleResizeStart(e, paneId)}
        onPointerMove={handleResizeMove}
        onPointerUp={handleResizeEnd}
        onPointerCancel={handleResizeEnd}
        style={{
          position: 'absolute', top: top - 14, left: '50%', transform: 'translateX(-50%)',
          width: 72, height: 28,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'auto', touchAction: 'none', cursor: 'ns-resize',
        }}
      >
        <span style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--color-muted)', opacity: 0.8 }} />
      </div>
    );
  });

  return (
    <>
    {/* Root div always renders so the ResizeObserver attaches on mount (even before bar data arrives) */}
    <div
      ref={rootRef}
      style={{ position: 'absolute', inset: 0, zIndex: Z_INDEX.chartOverlay, pointerEvents: 'none' }}
    >
    {/* Main pane overlay: OHLCV + candle-pane indicator rows + compare rows (only when data is ready) */}
    {bar && (
      <div
        ref={legendBoxRef}
        style={{
          position: 'absolute', top: 0, left: 0, padding: '6px 10px',
          paddingLeft: (indicatorPaneMap.has('EPS_LINE') && !hiddenInds.has('EPS_LINE') && !isIntradayResolution(timeframe) && timeframe !== '1M')
            ? `${EPS_AXIS_WIDTH + 10}px` : '10px',
          // Cap the overlay so a long stack of indicator/compare rows can never
          // hide the whole candle pane behind it — scroll internally past that.
          maxHeight: candlePaneHeight != null ? Math.max(120, Math.floor(candlePaneHeight * 0.6)) : undefined,
          overflowY: 'auto',
          pointerEvents: legendScrollable ? 'auto' : 'none',
        }}
      >
        {/* OHLCV row */}
        {isMobile ? (
          /* Mobile: compact single-line OHLCV */
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap', overflow: 'hidden' }}>
            {symbolLogo && (
              <img
                src={symbolLogo}
                alt=""
                style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text)', lineHeight: 1.3, whiteSpace: 'nowrap' }}>{symbolName || symbol}</span>
            <span style={{ fontSize: '11px', color: 'var(--color-muted)', lineHeight: 1.3 }}>{timeframe}</span>
            <span style={{ fontSize: '12px', fontFamily: 'monospace', color, lineHeight: 1.3, whiteSpace: 'nowrap' }}>{fmt(close)}</span>
            {change != null && changePct != null && (
              <span style={{ fontSize: '12px', fontFamily: 'monospace', color, lineHeight: 1.3, whiteSpace: 'nowrap' }}>{fmtPct(changePct)}</span>
            )}
            {volume != null && volume > 0 && (
              <span style={{ fontSize: '11px', fontFamily: 'monospace', lineHeight: 1.3, whiteSpace: 'nowrap', marginLeft: 'auto' }}><span style={{ color: 'var(--color-muted)' }}>V</span><span style={{ color }}> {fmtVol(volume)}</span></span>
            )}
          </div>
        ) : (
          /* Desktop: full OHLCV */
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            {symbolLogo && (
              <img
                src={symbolLogo}
                alt=""
                style={{ width: 20, height: 20, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            {symbolName && (
              <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-text)', lineHeight: 1.3 }}>{symbolName}</span>
            )}
            <span style={{ fontSize: '13px', color: 'var(--color-muted)', lineHeight: 1.3 }}>{timeframe}</span>
            <span style={{ fontSize: '13px', color: 'var(--color-muted)', lineHeight: 1.3, margin: '0 2px' }}>·</span>
            <span style={{ fontSize: '13px', fontFamily: 'monospace', color, lineHeight: 1.3 }}><span style={{ color: 'var(--color-muted)' }}>O</span> {fmt(open)}</span>
            <span style={{ fontSize: '13px', fontFamily: 'monospace', color, lineHeight: 1.3 }}><span style={{ color: 'var(--color-muted)' }}>H</span> {fmt(high)}</span>
            <span style={{ fontSize: '13px', fontFamily: 'monospace', color, lineHeight: 1.3 }}><span style={{ color: 'var(--color-muted)' }}>L</span> {fmt(low)}</span>
            <span style={{ fontSize: '13px', fontFamily: 'monospace', color, lineHeight: 1.3 }}><span style={{ color: 'var(--color-muted)' }}>C</span> {fmt(close)}</span>
            {volume != null && volume > 0 && (
              <span style={{ fontSize: '13px', fontFamily: 'monospace', lineHeight: 1.3 }}><span style={{ color: 'var(--color-muted)' }}>V</span><span style={{ color }}> {fmtVol(volume)}</span></span>
            )}
            {change != null && changePct != null && (
              <span style={{ fontSize: '13px', fontFamily: 'monospace', color, lineHeight: 1.3 }}>{fmtChange(change)} ({fmtPct(changePct)})</span>
            )}
          </div>
        )}

        {/* Main-pane indicator rows (MA, EMA, BOLL, SAR) */}
        {mainPaneInds.map((def) => renderIndRow(def))}

        {/* Compare symbols row */}
        {compareSymbols.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', marginTop: '1px', pointerEvents: 'auto' }}>
            {(isMobile ? compareSymbols.slice(0, 1) : compareSymbols).map((sym, i) => {
              const chipColor = COMPARE_COLORS_EXPORT[i % COMPARE_COLORS_EXPORT.length];
              const { price, pct } = getCompareInfo(i);
              return (
                <span key={sym} style={{ display: 'inline-flex', alignItems: 'baseline', gap: '3px', fontSize: '13px', fontFamily: 'monospace', lineHeight: 1.3 }}>
                  <span style={{ color: chipColor, fontWeight: 600 }}>{sym}</span>
                  {price != null && (
                    <span style={{ color: chipColor }}>
                      {price.toFixed(2)}
                      {pct != null && <span style={{ color: chipColor, opacity: 0.85 }}> ({pct >= 0 ? '+' : ''}{pct.toFixed(2)}%)</span>}
                    </span>
                  )}
                  <button
                    onClick={() => { removeCompareSymbol(sym); trackCompareAction('remove', sym); }}
                    title={`Remove ${sym}`}
                    style={{ color: chipColor, opacity: 0.5, fontSize: '15px', lineHeight: 1, cursor: 'pointer', background: 'none', border: 'none', padding: '0 1px', fontFamily: 'sans-serif' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.5'; }}
                  >×</button>
                </span>
              );
            })}
            {isMobile && compareSymbols.length > 1 && (
              <span style={{ fontSize: '11px', color: 'var(--color-muted)', lineHeight: 1.3 }}>+{compareSymbols.length - 1} more</span>
            )}
          </div>
        )}

        {/* RS Line legend rows — shown for all configs when RS_LINE is on chart */}
        {indicatorPaneMap.has('RS_LINE') && rsLineConfigs.length > 0 && (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '2px', pointerEvents: 'auto' }}
            onMouseEnter={() => setHoveredInd('RS_LINE')}
            onMouseLeave={() => setHoveredInd(null)}
          >
            {(isMobile ? rsLineConfigs.slice(0, 1) : rsLineConfigs).map((cfg) => {
              const renderCfg = window.__rs_lines?.find((r) => r.id === cfg.id);
              const rating    = renderCfg?.rsRating ?? 0;
              const label     = renderCfg?.label ?? cfg.benchmarkSymbol;
              const dimmed    = !cfg.visible;
              return (
                <span
                  key={cfg.id}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontFamily: 'monospace', lineHeight: 1.3, opacity: dimmed ? 0.4 : 1, transition: 'opacity 0.15s' }}
                >
                  {/* Color swatch — dashed when hidden */}
                  <span style={{ display: 'inline-block', width: '18px', height: '2px', background: dimmed ? 'transparent' : cfg.color, border: dimmed ? `1px dashed ${cfg.color}` : 'none', borderRadius: '1px', flexShrink: 0 }} />
                  {/* Label */}
                  <span style={{ color: cfg.color, fontWeight: 600 }}>{label}</span>
                  {/* RS Rating badge — only when visible */}
                  {!dimmed && (
                    <span style={{ fontSize: '11px', fontWeight: 700, color: cfg.color, border: `1px solid ${cfg.color}`, borderRadius: '3px', padding: '0px 4px', background: `${cfg.color}18`, lineHeight: 1.6, letterSpacing: '0.02em' }}>
                      RS {rating > 0 ? rating : '—'}
                    </span>
                  )}
                  {/* Eye toggle — per line */}
                  <button
                    title={dimmed ? 'Show RS line' : 'Hide RS line'}
                    onClick={() => updateRSLine(cfg.id, { visible: !cfg.visible })}
                    style={{ color: cfg.color, opacity: 0.55, lineHeight: 1, cursor: 'pointer', background: 'none', border: 'none', padding: '0 1px' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.55'; }}
                  >
                    {dimmed ? <EyeOffIcon size={13} /> : <EyeIcon size={13} />}
                  </button>
                </span>
              );
            })}
            {isMobile && rsLineConfigs.length > 1 && (
              <span style={{ fontSize: '11px', color: 'var(--color-muted)', lineHeight: 1.3 }}>+{rsLineConfigs.length - 1} more</span>
            )}
            {/* Shared gear + X — animated in on hover, matching MA legend behaviour */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '3px',
              opacity: (hoveredInd === 'RS_LINE' || isMobile) ? 1 : 0,
              transform: (hoveredInd === 'RS_LINE' || isMobile) ? 'translateX(0)' : 'translateX(-6px)',
              transition: 'opacity 150ms ease, transform 150ms ease',
              pointerEvents: (hoveredInd === 'RS_LINE' || isMobile) ? 'auto' : 'none',
            }}>
              <button
                title="RS Line settings"
                onClick={() => setRsModalOpen(true)}
                style={{ color: 'var(--color-muted)', opacity: 0.7, lineHeight: 1, cursor: 'pointer', background: 'none', border: 'none', padding: '0 2px' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.7'; }}
              >
                <GearIcon size={13} />
              </button>
              <button
                title="Remove all RS lines"
                onClick={() => rsLineConfigs.forEach((c) => removeRSLine(c.id))}
                style={{ color: 'var(--color-muted)', opacity: 0.7, lineHeight: 1, cursor: 'pointer', background: 'none', border: 'none', padding: '0 2px' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.7'; }}
              >
                <XIcon size={13} />
              </button>
            </span>
          </div>
        )}
      </div>
    )}

    {/* vol_pane label — top-left of the vol pane; uses renderIndRow to show MA volume lines.
        Rendered on mobile too so the eye/gear/remove buttons stay reachable — numeric
        values are suppressed on mobile (see the "Default entries" guard above) since
        they're shown via the right-axis labels instead. */}
    {volActive && paneTopMap.has('vol_pane') && (
      <div style={{ position: 'absolute', top: paneTopMap.get('vol_pane'), left: 0, right: 0, pointerEvents: 'none' }}>
        {renderIndRow(INDICATORS.find((i) => i.name === 'VOL')!, true)}
      </div>
    )}

    {/* User sub-pane labels — each at the top-left of its own pane.
        Rendered on mobile too: action buttons (close/eye/gear) have no other home,
        and custom legends (e.g. FOREIGN_HOLDING) aren't duplicated by the right-axis
        labels, which only cover plain numeric entries. */}
    {userSubPaneInds.map((def) => {
      const paneId = indicatorPaneMap.get(def.name);
      const top    = paneId ? paneTopMap.get(paneId) : undefined;
      if (top == null) return null;
      return (
        <div
          key={def.name}
          style={{ position: 'absolute', top, left: 0, right: 0, pointerEvents: 'none' }}
        >
          {renderIndRow(def, true)}
        </div>
      );
    })}

    {/* Macro sub-pane labels */}
    {macroSubPaneNames.map((indName) => {
      const paneId = indicatorPaneMap.get(indName);
      const top = paneId ? paneTopMap.get(paneId) : undefined;
      if (top == null) return null;
      return (
        <div
          key={indName}
          style={{ position: 'absolute', top, left: 0, right: 0, pointerEvents: 'none' }}
        >
          {renderMacroIndRow(indName)}
        </div>
      );
    })}

    {/* Right-axis value labels — main pane */}
    {renderPaneAxisValues('candle_pane', 0)}

    {/* Right-axis value labels — vol pane */}
    {volActive && paneTopMap.has('vol_pane') && renderPaneAxisValues('vol_pane', paneTopMap.get('vol_pane')!)}

    {/* Right-axis value labels — user sub-panes */}
    {userSubPaneInds.map((def) => {
      const paneId = indicatorPaneMap.get(def.name);
      const top    = paneId ? paneTopMap.get(paneId) : undefined;
      if (top == null) return null;
      return renderPaneAxisValues(paneId!, top);
    })}

    {/* Right-axis value labels — macro sub-panes */}
    {macroSubPaneNames.map((indName) => {
      const paneId = indicatorPaneMap.get(indName);
      const top    = paneId ? paneTopMap.get(paneId) : undefined;
      if (top == null) return null;
      return renderPaneAxisValues(paneId!, top);
    })}

      {rsModalOpen && (
        <Suspense fallback={null}>
          <RSConfigModal isActive={rsLineConfigs.some((c) => c.visible)} onClose={() => setRsModalOpen(false)} />
        </Suspense>
      )}

      {epsLineModalOpen && <EpsLineConfigModal isActive={indicatorPaneMap.has('EPS_LINE')} onClose={() => setEpsLineModalOpen(false)} />}

      {cupHandleModalOpen && <CupHandleConfigModal isActive={indicatorPaneMap.has('CUP_HANDLE')} onClose={() => setCupHandleModalOpen(false)} />}

      {oneilSellModalOpen && <WilliamOneilSellConfigModal isActive={indicatorPaneMap.has('WILLIAM_ONEIL_SELL')} onClose={() => setOneilSellModalOpen(false)} />}
      {foreignHoldingModalOpen && <ForeignHoldingConfigModal isActive={indicatorPaneMap.has('FOREIGN_HOLDING')} onClose={() => setForeignHoldingModalOpen(false)} />}

      {configuring && (
        <Suspense fallback={null}>
        <IndicatorConfigModal
          ind={configuring}
          isActive={indicatorPaneMap.has(configuring.name)}
          currentParams={indicatorParams[configuring.name] ?? configuring.defaultParams ?? []}
          currentColors={indicatorColors[configuring.name] ?? INDICATOR_DEFAULT_COLORS[configuring.name] ?? []}
          currentScanConfigs={indicatorScanConfigs[configuring.name] ?? []}
          volumeLabelsEnabled={volumeLabelEnabled}
          crossVolumeEnabled={crossVolumeEnabled}
          rsiZonesEnabled={rsiZonesEnabled}
          rsiDivergenceEnabled={rsiDivergenceEnabled}
          rsiDivergencePeriod={rsiDivergencePeriod}
          rsiDivergencePivotLookback={rsiDivergencePivotLookback}
          onAdd={(params, colors) => handleLegendUpdate(configuring, params, colors)}
          onUpdate={(params, colors) => handleLegendUpdate(configuring, params, colors)}
          onPreviewUpdate={(params, colors) => handleLegendPreviewUpdate(configuring, params, colors)}
          onRemove={() => handleLegendRemove(configuring)}
          onClose={() => setConfiguring(null)}
          onScanUpdate={(configs) => {
            setIndicatorScanConfigs(configuring.name, configs);
            try { chart?.overrideIndicator({ name: 'VOL_LABELS' } as never, 'vol_pane'); } catch { /* ignore */ }
          }}
          onVolumeLabelsToggle={(enabled) => {
            setVolumeLabelEnabled(enabled);
            try { chart?.overrideIndicator({ name: 'VOL_LABELS' } as never, 'vol_pane'); } catch { /* ignore */ }
          }}
          onCrossVolumeToggle={(enabled) => {
            setCrossVolumeEnabled(enabled);
            try { chart?.overrideIndicator({ name: 'VOL' } as never, 'vol_pane'); } catch { /* ignore */ }
            try { chart?.overrideIndicator({ name: 'VOL_LABELS' } as never, 'vol_pane'); } catch { /* ignore */ }
            try { chart?.overrideIndicator({ name: 'CROSS_VOL' } as never, 'vol_pane'); } catch { /* ignore */ }
          }}
          onRsiZonesToggle={(enabled) => {
            setRsiZonesEnabled(enabled);
            const paneId = indicatorPaneMap.get('RSI');
            if (paneId) { try { chart?.overrideIndicator({ name: 'RSI_ZONES', styles: {} } as never, paneId); } catch { /* ignore */ } }
          }}
          onRsiDivergenceToggle={(enabled) => {
            setRsiDivergenceEnabled(enabled);
            const paneId = indicatorPaneMap.get('RSI');
            if (paneId) { try { chart?.overrideIndicator({ name: 'RSI_DIVERGENCE', styles: {} } as never, paneId); } catch { /* ignore */ } }
          }}
          onRsiDivergenceParamsChange={(period, pivotLookback) => {
            setRsiDivergencePeriod(period);
            setRsiDivergencePivotLookback(pivotLookback);
            const paneId = indicatorPaneMap.get('RSI');
            if (paneId) { try { chart?.overrideIndicator({ name: 'RSI_DIVERGENCE', calcParams: [period, pivotLookback] } as never, paneId); } catch { /* ignore */ } }
          }}
        />
        </Suspense>
      )}
    </div>

    {/* klinecharts' own pane dividers are z-index 20 (7px strips); this layer sits just above
        them so the handles win touches at the boundary. Empty on desktop. */}
    {resizeHandles.length > 0 && (
      <div style={{ position: 'absolute', inset: 0, zIndex: 21, pointerEvents: 'none' }}>
        {resizeHandles}
      </div>
    )}
    </>
  );
}
